import http from '@/lib/http'
import { useMutation } from '@tanstack/react-query'
import { queryClient } from '@/services/queryClient'
import type { EditPatientFormData } from '@/components/pages/medication-request/sections/patient-data'
import type { MedRequest } from '@/types/data'
import { useToast } from './use-toast'
import { isUserTrialPrescriber } from '@/lib/permissions'

const useUpdatePatient = ({ medReqId, patientId }: { medReqId: string; patientId: string }) => {
  const { toast } = useToast()
  const isTrialPrescriber = isUserTrialPrescriber()

  return useMutation({
    mutationKey: ['updatePatient', patientId],
    mutationFn: (formData: EditPatientFormData) => {
      // Only perform actual mutation actions if the prescriber is not a trial prescriber.
      if (isTrialPrescriber) {
        return Promise.resolve(null)
      }

      return http.post(`/patients/${patientId}/update-details`, {
        family_history: formData.familyHistory,
        other: formData.otherObservations,
        social_history: formData.socialHistory,
        allergies: formData.allergies,
        conditions: formData.conditions,
        medications: formData.medications
      })
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData(['medicationRequestById', medReqId], (data: MedRequest) => {
        return {
          ...data,
          patient: {
            ...data.patient,
            ...variables
          }
        }
      })
    },
    onError: error => {
      console.error(error)
      toast({
        title: 'Error updating patient',
        description: 'Please try again',
        variant: 'destructive'
      })
    }
  })
}

export default useUpdatePatient
